import { useEffect, useState, type JSX } from 'react';

import { CustomHourglass } from 'DesignSystem2/Icon/CustomIcons/CustomHourglass';
import Palette from 'DesignSystem2/Theme/Palette';
import Title from 'DesignSystem3/Text/Title';

interface CountdownTimerProps {
    closeDate: Date;
}

const CountdownTimer = ({ closeDate }: CountdownTimerProps): JSX.Element => {
    // Start null so the server render and first client render agree (both render
    // nothing). A live countdown depends on the current time, which differs between
    // SSR and hydration, so we only compute it on the client after mount.
    const [timeUntilClose, setTimeUntilClose] = useState<number | null>(null);

    useEffect(() => {
        setTimeUntilClose(closeDate.getTime() - new Date().getTime());
        const interval = setInterval(() => {
            setTimeUntilClose(closeDate.getTime() - new Date().getTime());
        }, 250);
        return () => clearInterval(interval);
    }, [closeDate]);

    if (timeUntilClose === null || timeUntilClose <= 0 || timeUntilClose > 1000 * 60 * 60 * 24) {
        return <></>;
    }

    // Format h:mm:ss
    const hours = Math.floor(timeUntilClose / (1000 * 60 * 60));
    const minutes = Math.floor((timeUntilClose % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeUntilClose % (1000 * 60)) / 1000);
    const minutesAndSeconds = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    const timeString = hours > 0 ? `${hours}:${minutesAndSeconds}` : minutesAndSeconds;

    const icon = <CustomHourglass sx={{ fontSize: 18, color: Palette.Orange.x10 }} />;

    if (hours === 0) {
        return (
            <span className="inline-flex items-center gap-0.5">
                <Title.x20 className="typ-tabular text-orange-x10">{timeString}</Title.x20>
                {icon}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-0.5">
            <Title.x10 className="typ-tabular text-orange-x10">{timeString}</Title.x10>
            {icon}
        </span>
    );
};

export default CountdownTimer;
